import { ApolloLink, Observable, type FetchResult } from "@apollo/client";
import { getOperationDefinition } from "@apollo/client/utilities";
import { PROD } from "utils/config";
import { ENV } from "utils/hostEnv";
import {
  injectionStore,
  type CapturedError,
  type OperationType,
  type ResolvedInjection,
} from "./injectionStore";

// Single source of truth for whether the response-overrides feature is
// active. Used both by this link (which becomes a passthrough when false)
// and by the panel mount site, so the rest of the codebase doesn't need
// to know about gating.
export const RESPONSE_OVERRIDE_ENABLED = ENV.NAME !== PROD;

function extractRealError(result: FetchResult): CapturedError | null {
  if (!result.errors?.length) return null;
  const first = result.errors[0];
  const ext = first.extensions as Record<string, unknown> | undefined;
  const status = typeof ext?.status === "number" ? ext.status : undefined;
  return {
    message: first.message,
    status,
    path: first.path,
    extensions: ext,
    source: "graphql",
    capturedAt: Date.now(),
  };
}

// Deep clone via JSON so the snapshot is decoupled from Apollo's cache — the
// cache can rewrite the live `data` object after a later mutation, and we
// don't want the template to silently mutate underneath the editor.
function safeClone(value: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return undefined;
  }
}

// Whether the resolved injection will actually change the response. The
// envelope variant needs to parse — invalid JSON falls back to the real
// response (the panel flags the parse error next to the editor, so the
// user sees what's wrong without their app exploding on a typo).
function isApplicable(resolved: ResolvedInjection): boolean {
  if (resolved.kind === "synthetic-error") return true;
  const text = resolved.customData.trim();
  if (!text) return false;
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

function applyResolved(
  resolved: ResolvedInjection,
  result: FetchResult,
  operationName: string,
): FetchResult {
  if (resolved.kind === "synthetic-error") {
    return {
      ...result,
      data: null,
      errors: [
        {
          message: resolved.message,
          path: [operationName],
          extensions: { status: resolved.status, simulated: true },
        },
      ],
    };
  }
  // envelope — full `{ data?, errors? }` shape, authored by the user.
  try {
    const parsed = JSON.parse(resolved.customData) as {
      data?: FetchResult["data"];
      errors?: FetchResult["errors"];
    };
    return { ...result, data: parsed.data, errors: parsed.errors };
  } catch {
    return result;
  }
}

// Sits between `errorLink` and `timeoutLink` in the Apollo client chain.
// On every response it:
//   1. Notifies `injectionStore` so the dev panel can show the operation.
//   2. If a rule resolves for the operation, overrides the result so the
//      real `errorLink` and downstream consumers handle it exactly as if
//      the backend had returned it.
// Self-gates on `RESPONSE_OVERRIDE_ENABLED` — in prod the link is wired
// into the chain but immediately forwards, so the rest of the codebase
// doesn't need to conditional anything.
export const responseOverrideLink = new ApolloLink((operation, forward) => {
  if (!RESPONSE_OVERRIDE_ENABLED) return forward(operation);

  const startedAt = Date.now();
  const def = getOperationDefinition(operation.query);
  const opType: OperationType = def?.operation ?? "unknown";

  injectionStore.recordStart(operation.operationName, opType);

  return new Observable((observer) => {
    // Short-circuit mutations whenever a rule is going to apply: the user
    // has handed us a complete response, so hitting the server would just
    // mutate state we're about to ignore. For queries, executing then
    // overwriting is harmless (and lets the panel keep `lastData` fresh).
    if (opType === "mutation") {
      const earlyResolved = injectionStore.resolveInjection(
        operation.operationName,
        opType,
      );
      if (earlyResolved && isApplicable(earlyResolved)) {
        const synthetic = applyResolved(
          earlyResolved,
          { data: null },
          operation.operationName,
        );
        injectionStore.recordEnd(
          operation.operationName,
          "simulated",
          Date.now() - startedAt,
          null,
        );
        observer.next(synthetic);
        observer.complete();
        return;
      }
    }

    const sub = forward(operation).subscribe({
      next: (result) => {
        const resolved = injectionStore.resolveInjection(
          operation.operationName,
          opType,
        );
        const durationMs = Date.now() - startedAt;
        // Capture the real error shape regardless of whether we're about to
        // overwrite it with an injection — that way the panel can surface
        // backend errors even while a rule is masking them.
        const realError = extractRealError(result);
        // Snapshot the real data (deep-cloned so future cache mutations
        // can't reach in) only when nothing is being injected — otherwise
        // we'd overwrite the template with whatever fake data we just
        // produced.
        const willInject = resolved !== null && isApplicable(resolved);
        const capturedData =
          !willInject && !realError && result.data !== undefined
            ? safeClone(result.data)
            : undefined;
        if (willInject && resolved) {
          observer.next(
            applyResolved(resolved, result, operation.operationName),
          );
          injectionStore.recordEnd(
            operation.operationName,
            "simulated",
            durationMs,
            realError,
          );
        } else if (realError) {
          injectionStore.recordEnd(
            operation.operationName,
            "errored",
            durationMs,
            realError,
          );
          observer.next(result);
        } else {
          injectionStore.recordEnd(
            operation.operationName,
            "ok",
            durationMs,
            null,
            capturedData,
          );
          observer.next(result);
        }
      },
      error: (err) => {
        const captured: CapturedError = {
          message: err.message || "Network error",
          extensions: { name: err.name },
          source: "network",
          capturedAt: Date.now(),
        };
        injectionStore.recordEnd(
          operation.operationName,
          "errored",
          Date.now() - startedAt,
          captured,
        );
        observer.error(err);
      },
      complete: () => observer.complete(),
    });
    return () => sub.unsubscribe();
  });
});
